import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  EventEmitter,
  inject,
  Injector,
  Input,
  OnInit,
  Output,
  signal,
  ViewChild,
  WritableSignal,
} from '@angular/core';
import { AbstractControl } from '@angular/forms';
import { AsyncPipe, NgClass, NgIf } from '@angular/common';
import { TooltipDirective } from '@tpt/common/tooltips/tooltip';
import { OverflowTooltipDirective } from '@tpt/common/tooltips/overflow';

import { FormFieldConfig } from '@app/shared/forms/dynamic-form/fields.service';
import { Field } from '@app/origami-api/constants/field-flag';
import { SessionService } from '@app/session/session.service';
import { Observable, of, startWith, Subscription } from 'rxjs';
import { map } from 'rxjs/operators';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { HtmlContentComponent } from '@app/shared/misc/html-content/html-content.component';

@Component({
  selector: 'ti-form-field-label',
  standalone: true,
  imports: [NgIf, NgClass, AsyncPipe, TooltipDirective, OverflowTooltipDirective, HtmlContentComponent],
  styleUrls: ['./form-field-label.component.sass'],
  templateUrl: './form-field-label.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FormFieldLabelComponent implements OnInit {
  private injector = inject(Injector);
  private destroyRef = inject(DestroyRef);
  private sessionService = inject(SessionService);

  @Input() self: AbstractControl;
  @Input() config: FormFieldConfig;
  @Input() isDisabledGetter: () => boolean;
  @Input() set isCopyMode(v: { value: WritableSignal<boolean> }) {
    if (this.isCopyValueSubscription) {
      this.isCopyValueSubscription.unsubscribe();
    }

    if (v?.value) {
      this.isCopyValueSubscription = toObservable(v?.value, {
        injector: this.injector,
      })
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(this.isCopyValue.set);
    }
  }

  @Output() onCopyClick = new EventEmitter<{ fieldName: string; isSelected: boolean }>();

  @ViewChild(TooltipDirective) public tooltipDir: TooltipDirective;

  get isWithTooltip(): boolean {
    return !!(this.config.UIData && this.config.UIData.defaultValueFromCustomForm);
  }

  get isClickable(): boolean {
    if (!this.isWithTooltip) {
      return false;
    }

    return this.sessionService.room?.settings?.documentTypes.common.allowModifyDefaultValues.get();
  }

  isSelected: boolean;
  isCopyValueSubscription: Subscription;
  isCopyValue: WritableSignal<boolean> = signal(false);

  public readonly miscStrings = tiStrings.app.misc;

  protected isRequired$: Observable<boolean> = null;

  ngOnInit() {
    this.isRequired$ = (
      this.self
        ? this.self.statusChanges.pipe(startWith(Field.Is.required(this.config)))
        : of(Field.Is.required(this.config))
    ).pipe(
      map((isRequired) => {
        if (typeof isRequired === 'boolean') {
          return isRequired;
        } else {
          // let's check the runtime status
          const validationResult = this.self.validator && this.self.validator(<AbstractControl>{});
          return validationResult && validationResult.required;
        }
      }),
    );
  }

  copyClick() {
    this.isSelected = !this.isSelected;

    this.onCopyClick.emit({ fieldName: this.config.fieldName, isSelected: this.isSelected });
  }

  onLabelClick() {
    if (!this.isClickable) {
      return;
    }

    delete this.config.UIData.defaultValueFromCustomForm; // this will affect the related field

    this.tooltipDir.hide();
  }

  getLabelTooltipText(): string {
    if (this.isClickable) {
      if (this.config.UIData?.hasActualDbValue || this.config.UIData?.isNewEntity) {
        return this.miscStrings.defaultValueFormTooltip;
      } else {
        return this.miscStrings.defaultValueFormTooltipNoData;
      }
    } else {
      if (this.config.UIData?.hasActualDbValue || this.config.UIData?.isNewEntity) {
        return this.miscStrings.defaultValueFormTooltipNoEdit;
      } else {
        return this.miscStrings.defaultValueFormTooltipNoEditNoData;
      }
    }
  }

  isAttentionField(): boolean {
    return (
      this.config.UIData?.defaultValueFromCustomForm &&
      !this.config.UIData?.hasActualDbValue &&
      !this.config.UIData?.isNewEntity
    );
  }
}
